import { reducerWithInitialState } from 'typescript-fsa-reducers/dist'

import { CollectionItem, Collection, Coordinates } from '../api/types'
import * as actions from './actions'
import * as apiActions from '../api/actions'
import { appLanguageSelected, setInitialisedRentalMode } from '../settings/actions'
import { cloneMap } from '../root/functions'

export interface StoreState {
	readonly collectionDetails: Map<number, CollectionDetailsState>

	readonly collectionItems: Map<number, CollectionItemsState>
}

export interface CollectionDetailsState {
	details?: Collection
	version?: number
	loading: boolean
	error?: Error
}

interface CollectionItemsState {
	items?: CollectionItem[]
	version?: number
	loading: boolean
	error?: Error
}

const INITIAL_STATE: StoreState = {
	collectionDetails: new Map(),
	collectionItems: new Map(),
}

export const reducer = reducerWithInitialState(INITIAL_STATE)
	// FetchCollectionitems - Disk/Memory
	.case(
		actions.loadedCollectionItemsFromDisk,
		(state, payload): StoreState => {
			const updatedItems = cloneMap(state.collectionItems)
			updatedItems.set(payload.id, { items: payload.items, version: payload.version, loading: false })

			return {
				...state,
				collectionItems: updatedItems,
			}
		},
	)
	// FetchCollectionItems - Network
	.case(
		apiActions.fetchCollectionItems.started,
		(state, payload): StoreState => {
			// if downloading collection as part of content syncing, we dont want to cache on redux, just download to disk
			if (payload.isPreCaching) {
				return state
			}

			const updatedItems = cloneMap(state.collectionItems)
			updatedItems.set(payload.collectionId, { loading: true })

			return {
				...state,
				collectionItems: updatedItems,
			}
		},
	)
	.case(
		apiActions.fetchCollectionItems.done,
		(state, payload): StoreState => {
			if (payload.params.isPreCaching) {
				return state
			}

			const updatedItems = cloneMap(state.collectionItems)
			updatedItems.set(payload.params.collectionId, {
				items: payload.result.data,
				version: payload.params.collectionVersion,
				loading: false,
			})

			return {
				...state,
				collectionItems: updatedItems,
			}
		},
	)
	.case(
		apiActions.fetchCollectionItems.failed,
		(state, payload): StoreState => {
			if (payload.params.isPreCaching) {
				return state
			}

			const updatedItems = cloneMap(state.collectionItems)
			updatedItems.set(payload.params.collectionId, { error: payload.error, loading: false })

			return {
				...state,
				collectionItems: updatedItems,
			}
		},
	)

	// FetchCollectionItems - Clear Error
	.case(
		actions.clearLoadCollectionErrorAction,
		(state, collectionId): StoreState => {
			// clone map
			const updatedItems = cloneMap(state.collectionItems)
			// delete collection from map (since it should be in error state)
			updatedItems.delete(collectionId)
			return {
				...state,
				collectionItems: updatedItems,
			}
		},
	)

	// FetchCollectionDetails - Disk/Memory
	.case(
		actions.loadedCollectionDetailsFromDisk,
		(state, payload): StoreState => {
			const updatedDetails = cloneMap(state.collectionDetails)
			updatedDetails.set(payload.id, { details: payload.collection, version: payload.version, loading: false })

			return {
				...state,
				collectionDetails: updatedDetails,
			}
		},
	)
	// FetchCollectionDetails - Network
	.case(
		apiActions.fetchCollection.started,
		(state, payload): StoreState => {
			// if downloading screen as part of content syncing, we dont want to cache on redux, just download to disk
			if (payload.isPreCaching) {
				return state
			}

			const updatedDetails = cloneMap(state.collectionDetails)
			updatedDetails.set(payload.collectionId, { loading: true })

			return {
				...state,
				collectionDetails: updatedDetails,
			}
		},
	)
	.case(
		apiActions.fetchCollection.done,
		(state, payload): StoreState => {
			if (payload.params.isPreCaching) {
				return state
			}

			const updatedDetails = cloneMap(state.collectionDetails)
			updatedDetails.set(payload.params.collectionId, {
				details: payload.result,
				version: payload.params.collectionVersion,
				loading: false,
			})

			return {
				...state,
				collectionDetails: updatedDetails,
			}
		},
	)
	.case(
		apiActions.fetchCollection.failed,
		(state, payload): StoreState => {
			if (payload.params.isPreCaching) {
				return state
			}

			const updatedDetails = cloneMap(state.collectionDetails)
			updatedDetails.set(payload.params.collectionId, { error: payload.error, loading: false })

			return {
				...state,
				collectionDetails: updatedDetails,
			}
		},
	)

	// LANGUAGE CHANGED
	.case(
		appLanguageSelected,
		(): StoreState =>
			// clear all cached files as we do not know what language they are
			INITIAL_STATE,
	)

	// RENTAL MODE
	.case(setInitialisedRentalMode, (state, payload): StoreState => {
		if (payload) {
			// return state unchanged as no action required
			return state
		} else {
			// reset all cached content
			return INITIAL_STATE
		}
	})
